/** @format */

import React, { useEffect, useState } from "react";
import axios from "axios";
import SubjectHeader from "../../components/ui/SubjectHeader";
import ListOfQuestion from "../../components/ui/ListOfQuestion";
import AddCustomQuestionModal from "../../components/ui/AddCustomQuestionModal";
import { useAuthStore } from "../../context/useAuthStore";
import toast from "react-hot-toast";
import TextInput from "../../components/ui/TextInput";
import { FiSearch } from "react-icons/fi";
import SelectInput from "../../components/ui/SelectInput";
import BareActModal from "../../components/ui/BareActModal";
import { Link } from "react-router-dom";
import { ROUTES } from "../../constant/routes";
import TCStatusModal from "../../components/ui/TCStatusModal";
import ExportAsPDFModal from "../../components/ui/ExportAsPDFModal";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const DashboardPage: React.FC = () => {
  const { user, token, selectedSubject, setSelectedSubject, clearSession } =
    useAuthStore();
  const [subjectOptions, setSubjectOptions] = useState<
    { subjectName: string; subjectCode: string }[]
  >([]);
  const [questions, setQuestions] = useState<
    { question: string; weightage: number }[]
  >([]);
  const [showModal, setShowModal] = useState(false);
  const [showBareActModal, setShowBareActModal] = useState(false);
  const [showTCStatusModal, setShowTCStatusModal] = useState(false);
  const [showExportPdfModal, setShowExportPdfModal] = useState(false);

  // Initialize state from sessionStorage if available
  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      const saved = sessionStorage.getItem("dashboardState");
      return saved ? JSON.parse(saved).searchQuery || "" : "";
    } catch {
      return "";
    }
  });

  const [loading, setLoading] = useState(false);

  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">(() => {
    try {
      const saved = sessionStorage.getItem("dashboardState");
      return saved ? JSON.parse(saved).sortOrder || "default" : "default";
    } catch {
      return "default";
    }
  });

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(
      "dashboardState",
      JSON.stringify({ searchQuery, sortOrder })
    );
  }, [searchQuery, sortOrder]);

  // 🧠 Filtered + Sorted Questions
  const filteredQuestions = questions.filter((q) =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    if (sortOrder === "asc") {
      return a.question.localeCompare(b.question);
    } else if (sortOrder === "desc") {
      return b.question.localeCompare(a.question);
    } else {
      return 0; // Default (no sorting)
    }
  });

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey &&
          e.shiftKey &&
          (e.key === "I" || e.key === "J" || e.key === "C")) ||
        (e.ctrlKey && ["u", "a", "c", "x", "v"].includes(e.key)) ||
        e.key === "F12"
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!user?.userId || !token) return;

      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/subscription?userId=${user?.userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        if (res.data.success) {
          const options = res.data.success.map(
            (item: { subjectName: string; subjectCode: string }) => ({
              subjectName: item.subjectName,
              subjectCode: item.subjectCode,
            })
          );
          setSubjectOptions(options);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch subjects";
          if (
            errorMessage.includes("Session expired") ||
            error.response?.status === 401
          ) {
            clearSession("expired");
          } else {
            toast.error(errorMessage);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [token, user?.userId, clearSession]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedSubject || !token) return;

      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/questionanswerbySubjectId`,
          {
            params: { subjectId: selectedSubject.subjectCode },
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-Requested-With": "XMLHttpRequest",
            },
            withCredentials: true,
          }
        );

        const questionsArray = res.data.success;

        if (Array.isArray(questionsArray)) {
          const questionList = questionsArray.map(
            (q: { questionReference: string; weightage: number }) => ({
              question: q.questionReference,
              weightage: q.weightage,
            })
          );
          setQuestions(questionList);
        } else {
          setQuestions([]);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch questions";
          if (
            errorMessage.includes("Session expired") ||
            error.response?.status === 401
          ) {
            clearSession("expired");
          } else {
            toast.error(errorMessage);
          }
        }
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [selectedSubject, token, clearSession]);

  const handleSubjectChange = (subjectCode: string) => {
    const subject =
      subjectOptions.find((s) => s.subjectCode === subjectCode) || null;
    setSelectedSubject(subject);

    // Reset search query and sort order on subject change
    setSearchQuery("");
    setSortOrder("default");
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleBareActModalClose = () => {
    setShowBareActModal(false);
  };
  const normalizedTcStatus = user?.tcStatus === "true" ? true : false;

  useEffect(() => {
    if (!normalizedTcStatus) {
      setShowTCStatusModal(true);
    }
  }, [normalizedTcStatus]);

  const handleTCStatusModalClose = () => {
    setShowTCStatusModal(false);
  };

  const handleExportPdfModalClose = () => {
    setShowExportPdfModal(false);
  };

  return (
    <>
      <section
        className="dashboardLayoutSubSection"
        style={{
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          userSelect: "none",
        }}
      >
        <SubjectHeader
          subject={selectedSubject?.subjectName}
          onAddCustomQuestion={() => setShowModal(true)}
          onSubjectChange={handleSubjectChange}
          subjectOptions={subjectOptions}
          selectedSubjectId={
            selectedSubject ? selectedSubject.subjectCode : null
          }
          onBareActClick={() => setShowBareActModal(!showBareActModal)}
          onExportPdfClick={() => setShowExportPdfModal(!showExportPdfModal)}
        />

        {loading && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
          </div>
        )}

        {selectedSubject ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 sm:grid-cols-3 gap-x-4 px-4 mb-4">
              <div className="w-full md:col-span-3 sm:col-span-2 col-span-1">
                <TextInput
                  id="searchQuery"
                  name="searchQuery"
                  label="Search Questions"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Questions..."
                  icon={FiSearch}
                  disabled={loading}
                  errorMessage={""}
                  required={false}
                  error={false}
                />
              </div>
              {/* Sorting dropdown */}
              <SelectInput
                name="sortOrder"
                label="Sort Questions"
                value={sortOrder}
                onChange={(value: string) =>
                  setSortOrder(value as "default" | "asc" | "desc")
                }
                options={[
                  { label: "Weightage", value: "default" },
                  { label: "Ascending (A–Z)", value: "asc" },
                  { label: "Descending (Z–A)", value: "desc" },
                ]}
                placeholder="Sort Questions..."
              />
              <p className="text-sm font-medium text-red-600 mt-2 col-span-1 sm:col-span-2 md:col-span-3 w-full">
                <span className="font-bold underline">Note:</span>
                <span className="pl-1">
                  Number indicates the weightage of the question. (Repetition of
                  questions)
                </span>
              </p>
              <p className="text-sm text-gray-600 mt-2 col-span-1 sm:col-span-1 md:col-span-1 w-full">
                {sortedQuestions.length} question
                {sortedQuestions.length !== 1 ? "s" : ""} found
              </p>
            </div>

            {sortedQuestions.length === 0 && searchQuery ? (
              <p className="text-lg font-semibold text-red-500 px-4">
                No questions found for "
                <span className="font-bold text-red-600">{searchQuery}</span>"
              </p>
            ) : (
              <ListOfQuestion
                questions={sortedQuestions}
                subject={selectedSubject.subjectName}
                subjectId={selectedSubject.subjectCode}
              />
            )}
          </>
        ) : (
          <div className="questionPlaceholder flex flex-col gap-1">
            <h3>
              Please select a subject from the dropdown to view the questions!
            </h3>
            <h3>
              No Subjects Yet?
              <Link to={ROUTES.SUBJECT} className="forgotPasswordLink">
                Add Subjects
              </Link>
            </h3>
          </div>
        )}
      </section>

      <AddCustomQuestionModal
        showModal={showModal}
        onClose={handleModalClose}
        subjectOptions={
          selectedSubject?.subjectName && selectedSubject?.subjectCode
            ? [
                {
                  label: selectedSubject.subjectName,
                  value: selectedSubject.subjectCode,
                },
              ]
            : []
        }
      />
      <BareActModal
        showModal={showBareActModal}
        onClose={handleBareActModalClose}
        subjectName={selectedSubject?.subjectName ?? ""}
        subjectCode={selectedSubject?.subjectCode ?? ""}
      />
      <ExportAsPDFModal
        showModal={showExportPdfModal}
        onClose={handleExportPdfModalClose}
        subjectName={selectedSubject?.subjectName ?? ""}
        subjectCode={selectedSubject?.subjectCode ?? ""}
      />
      {!normalizedTcStatus && (
        <TCStatusModal
          showModal={showTCStatusModal}
          onClose={handleTCStatusModalClose}
        />
      )}
    </>
  );
};

export default DashboardPage;
